import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal as AntModal, Tabs, ConfigProvider, Flex } from 'antd';

import {
  FolderOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import useGiide from '../../hooks/useQuery/useGiide';
import useGiideAudioLibrary from '../../hooks/useQuery/useGiideAudioLibrary';
import { setAudioUploadFileId } from '../../actions';

import AudioLibraryItem from './AudioLibraryItem';

import styles from './GiideWithStep.module.css';
import Dragger from 'antd/es/upload/Dragger';
import useAudioUpload from './Hooks/useAudioUpload';
import AudioLibraryUploadingItem from './AudioLibraryUploadingItem';
import useRecording from '../../hooks/useQuery/useRecording';

import { IState } from '../../graphql/types';
import { AudioLibOperations, AudioLibOperationsData } from '../../types';
import useRecordingOwnAudio from './Hooks/useRecordingOwnAudio';
import { colors, darkComponentsTheme } from '../../theme';

interface IProps {
  giideId: string;
  audioLibOperations: AudioLibOperations;
  audioLibOperationsData: AudioLibOperationsData;
}

const AudioTab: React.FC<IProps> = ({
  giideId,
  audioLibOperations,
  audioLibOperationsData,
}) => {
  const {
    handleChangeAudio,
    handleNextClick,
    setSelectedId,
    setShowChangeAudioModal,
  } = audioLibOperations;
  const { loading, changing, selectedId, showChangeAudioModal } =
    audioLibOperationsData;
  const { handleUpload, audioUploadFileName, audioUploadFileId } =
    useAudioUpload();
  const { openedStep } = useGiide(giideId);
  const { recording } = useRecording(openedStep?.id);
  const { TabPane } = Tabs;
  const dispatch = useDispatch();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const { data, giideAudioLibraryFetching } = useGiideAudioLibrary();
  const giideMaxFileSizeMBAudio = useSelector<IState, number>(
    state =>
      state.user?.plan?.features?.giideMaxFileSizeAudio
        ?.giideMaxFileSizeMBAudio ?? 0
  );
  const giideLengthInMinutes = useSelector<IState, number>(state =>
    state.user?.plan?.features?.giideMaxLengthAudio?.giideLengthInSeconds
      ? state.user.plan.features.giideMaxLengthAudio.giideLengthInSeconds / 60
      : 0
  );

  const { onwRecordingContent, showAudioLibrary, setShowAudioLibrary } =
    useRecordingOwnAudio(giideId);

  useEffect(() => {
    setSelectedId(
      recording?.libraryRecordId && !recording.deleted
        ? recording.libraryRecordId
        : null
    );
  }, [recording, setSelectedId]);

  useEffect(() => {
    // autoselect latest recording on giide load
    if (data && data.length > 0) {
      setSelectedId(id => {
        if (id === '' || !id) {
          return data[0]?.id || null;
        } else {
          return id;
        }
      });
    }
  }, [data, setSelectedId]);

  useEffect(() => {
    const uploadedLibItem = data?.find(item => item?.id === audioUploadFileId);
    if (
      uploadedLibItem &&
      audioUploadFileId &&
      audioUploadFileId === uploadedLibItem.id &&
      uploadedLibItem.state === 'finished'
    ) {
      setSelectedId(audioUploadFileId);
      dispatch(setAudioUploadFileId(undefined));
    }
  }, [audioUploadFileId, setSelectedId, data, dispatch]);

  const selectedRecordingNotReady = useMemo(
    () => data?.find(d => d?.id === selectedId)?.state !== 'finished',
    [data, selectedId]
  );

  const [loadingDraggedAudio, setLoadingDraggedAudio] = useState(false);

  return (
    <ConfigProvider theme={darkComponentsTheme}>
      <div className={styles.audioTabWrapper}>
        <AntModal
          className="changeAudioModal"
          centered
          closable
          open={showChangeAudioModal}
          onCancel={() => setShowChangeAudioModal(false)}
          footer={
            <div className="flexRight">
              <Button
                className={styles.changeAudioText}
                type={'default'}
                onClick={() => setShowChangeAudioModal(false)}
                disabled={changing || loading}
              >
                Cancel
              </Button>
              <Button
                type={'primary'}
                onClick={handleChangeAudio}
                disabled={changing || loading}
                loading={changing || loading}
              >
                Change Audio
              </Button>
            </div>
          }
        >
          <div className={styles.changeAudioText}>
            Are you sure you want to change the audio? If so, any feedcards
            you’ve already created will be saved, but you’ll have to re-link
            them to words in your new audio.
          </div>
        </AntModal>

        {!showAudioLibrary ? (
          <Tabs
            defaultActiveKey="1"
            centered
            className={`audio-tabs ${styles.audioTabs}`}
            tabBarExtraContent={
              <Button
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 0,
                  transform: 'translateY(25%)',
                  color: colors.white1,
                }}
                key={'audio-library-button'}
                type={'link'}
                id={'testing'}
                icon={<FolderOutlined />}
                onClick={() => setShowAudioLibrary(true)}
              >
                Audio Library
              </Button>
            }
          >
            <TabPane tab="Record Audio" key="1" className={styles.audioTab}>
              {onwRecordingContent()}
            </TabPane>
            <TabPane tab="Upload Audio" key="2" className={styles.audioTab}>
              <div className={styles.uploadAudioTab}>
                <Dragger
                  name="file"
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={async file => {
                    if (loadingDraggedAudio) {
                      return;
                    }

                    setLoadingDraggedAudio(true);
                    let result = await handleUpload(file);
                    setLoadingDraggedAudio(false);
                    if (result) {
                      setShowAudioLibrary(true);
                    }
                  }}
                  style={{ backgroundColor: 'transparent' }}
                >
                  {!loadingDraggedAudio ? (
                    <div style={{ marginTop: '24px' }}>
                      <UploadOutlined style={{ fontSize: '48px' }} />
                      <h2>Upload Audio</h2>
                      <div>
                        <p>Click to upload an audio file, or drag it here.</p>
                        <p>
                          MP3, MP4, WAV, or M4A {giideLengthInMinutes} minutes
                          (or {giideMaxFileSizeMBAudio}MB) max.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <LoadingOutlined spin style={{ fontSize: '48px' }} />
                  )}
                </Dragger>
              </div>
            </TabPane>
          </Tabs>
        ) : (giideAudioLibraryFetching.fetching &&
            !giideAudioLibraryFetching.refreshing) ||
          !data ? (
          <div className={styles.loadingWrapper}>
            <LoadingOutlined spin className={styles.loadingIcon} />
          </div>
        ) : (
          <>
            <div>
              <Flex vertical={true}>
                <div className={styles.libraryWrapper}>
                  <h2>My Audio Files</h2>
                  <ul>
                    {audioUploadFileName && <AudioLibraryUploadingItem />}

                    {data && data.length > 0 ? (
                      data.map(item => {
                        if (
                          !item ||
                          (audioUploadFileName && audioUploadFileId === item.id)
                        ) {
                          return null;
                        } else {
                          return (
                            <AudioLibraryItem
                              key={item.id}
                              item={item}
                              selectedId={selectedId}
                              setSelectedId={setSelectedId}
                              playingId={playingId}
                              setPlayingId={setPlayingId}
                            />
                          );
                        }
                      })
                    ) : (
                      <div style={{ color: colors.white1 }}>
                        No audio files uploaded yet. Once you record or upload
                        audio, it will appear here.
                      </div>
                    )}
                  </ul>

                  <Flex
                    gap={'large'}
                    justify={'flex-end'}
                    className={styles.libraryLastRow}
                  >
                    <Button
                      className={styles.libraryBackButton}
                      type={'default'}
                      onClick={() => setShowAudioLibrary(false)}
                    >
                      Back
                    </Button>

                    <Button
                      className={styles.audioNextBtn}
                      type={'primary'}
                      loading={loading}
                      disabled={
                        loading ||
                        !selectedId ||
                        !!audioUploadFileName ||
                        selectedRecordingNotReady
                      }
                      onClick={handleNextClick}
                    >
                      Next
                    </Button>
                  </Flex>
                </div>
              </Flex>
            </div>
          </>
        )}
      </div>
    </ConfigProvider>
  );
};

export default AudioTab;
